/**
 * HeadWithdrawModal Component
 * 본사 출금신청 모달 - 출금 금액 입력, 수수료 계산, 계좌 정보 표시
 */
import React, { useState, useCallback, useMemo } from 'react';
import { HeadWithdrawModalProps } from '../types/headWithdraw.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { formatAmountInput, formatAmount, parseAmountString } from '../utils/amountFormatter';
import WithdrawDetailsSection from './WithdrawDetailsSection';
import { AMOUNT_INPUT_STYLE, calculateFees } from '../utils/withdrawModal.helpers';
import '../styles/WithdrawModal.css';

const HeadWithdrawModal: React.FC<HeadWithdrawModalProps> = ({
    withdrawableAmount,
    withdrawFee = 300, // 기본값 300원
    accountInfo,
    onClose,
    onRequest
}) => {
    // ======== Hooks ========
    const { showToast } = useToast();

    // ======== State ========
    const [amount, setAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ======== Derived Values ========
    /** 입력된 금액의 숫자 값 */
    const numericAmount = useMemo(() => parseAmountString(amount), [amount]);

    /** 출금 가능 금액 */
    const availableAmount = withdrawableAmount?.balAmt || 0;

    /** 수수료 및 입금예정금액 계산 */
    const fees = useMemo(() => calculateFees(numericAmount, withdrawFee), [numericAmount, withdrawFee]);

    // ======== Event Handlers ========
    /** 모달 배경 클릭 시 닫기 */
    const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    /** 금액 입력 변경 - 숫자만 추출하여 천단위 콤마 포맷 적용 */
    const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(formatAmountInput(e.target.value));
    }, []);

    /** 출금 신청 제출 - 유효성 검사 후 요청 */
    const handleSubmit = useCallback(async () => {
        if (numericAmount <= 0) {
            showToast('출금 금액을 입력해주세요.', { type: ToastType.INFO });
            return;
        }

        // 총 차감금액 = 신청금액 + 수수료
        const totalDeduction = numericAmount + withdrawFee;
        if (totalDeduction > availableAmount) {
            showToast(`신청금액 + 수수료(${totalDeduction.toLocaleString()}원)가 출금가능금액(${availableAmount.toLocaleString()}원)을 초과합니다.`, { type: ToastType.INFO });
            return;
        }

        setIsSubmitting(true);
        try {
            await onRequest(numericAmount);
        } finally {
            setIsSubmitting(false);
        }
    }, [numericAmount, withdrawFee, availableAmount, onRequest, showToast]);

    // ======== Render ========
    return (
        <div className="modal" onClick={handleBackdropClick}>
            <div className="modal-content">
                {/* 헤더 */}
                <div className="modal-header">
                    <h2 className="modal-title">출금신청</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* 본문 */}
                <div className="modal-body">
                    {/* 출금가능금액 표시 */}
                    <div className="withdrawal-balance-section">
                        <div className="balance-label">출금가능금액</div>
                        <div className="balance-amount">₩ {formatAmount(availableAmount)}</div>
                        <div className="balance-info">
                            출금시 송금수수료 {withdrawFee.toLocaleString()}원이 부과됩니다.
                        </div>
                    </div>

                    {/* 금액 입력 섹션 */}
                    <div className="withdrawal-input-section">
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-input"
                                placeholder="출금 금액을 입력하세요"
                                value={amount}
                                onChange={handleAmountChange}
                                style={AMOUNT_INPUT_STYLE}
                                disabled={isSubmitting}
                            />
                            <button
                                className="btn btn-blue"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '처리중...' : '출금신청'}
                            </button>
                        </div>
                    </div>

                    {/* 출금 요청 내역 섹션 */}
                    <WithdrawDetailsSection
                        numericAmount={numericAmount}
                        fees={fees}
                        accountInfo={accountInfo}
                        withdrawableAmount={withdrawableAmount}
                    />
                </div>
            </div>
        </div>
    );
};

export default HeadWithdrawModal;
